import React, { useState } from "react";
import { auth, db } from "../firebase";
import { createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { logService } from "../utils/logService";
import Header from "./Header";

const Register = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const validate = () => {
    if (!displayName.trim()) {
      toast.error("Display name is required");
      return false;
    }
    if (!email) {
      toast.error("Email is required");
      return false;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter a valid email address");
      return false;
    }
    if (!password) {
      toast.error("Password is required");
      return false;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return false;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      const user = userCredential.user;
      await updateProfile(user, { displayName: displayName });

      // Create user document in Firestore with default role "User"
      await setDoc(doc(db, "users", userCredential.user.uid), {
        email: email,
        displayName: displayName,
        role: "User",
        status: "Active",
        verificationLevel: "Verified",
        createdAt: new Date().toISOString().split("T")[0],
        created_by_name: displayName,
        lastLogin: "Never",
        agentCount: 0,
      });

      // Log the user registration
      await logService.createLog(
        logService.actions.USER_REGISTERED,
        {
          email: email,
          displayName: displayName,
          uid: userCredential.user.uid,
        },
        {
          uid: userCredential.user?.uid,
          email: userCredential.user?.email,
          displayName: userCredential.user?.displayName || displayName,
        }
      );

      toast.success("Registration successful!");
      setTimeout(() => navigate("/dashboard"), 1000);
    } catch (err) {
      toast.error(err.message.replace("Firebase:", "").trim());
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-valia-background-primary flex flex-col">
      {/* Header */}
      <Header />

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-valia-primary mb-2">
              Create Account
            </h1>
            <p className="text-valia-gray-600 text-sm">Join Valia Platform</p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="valia-form"
            autoComplete="off"
          >
            <div className="valia-form-group">
              <div>
                <label htmlFor="displayName" className="valia-label">
                  Full Name
                </label>
                <input
                  id="displayName"
                  type="text"
                  placeholder="Enter your full name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="valia-input"
                  required
                  autoFocus
                />
              </div>

              <div>
                <label htmlFor="email" className="valia-label">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="valia-input"
                  required
                />
              </div>

              <div>
                <label htmlFor="password" className="valia-label">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  placeholder="Create password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="valia-input"
                  required
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="valia-label">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="valia-input"
                  required
                />
              </div>
            </div>

            <div className="mt-8">
              <button
                type="submit"
                className={`w-full valia-btn-primary valia-btn-sm ${
                  loading ? "valia-btn-loading" : ""
                }`}
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="valia-spinner w-4 h-4 mr-2"></div>
                    Creating Account...
                  </div>
                ) : (
                  "Create Account"
                )}
              </button>
            </div>

            <div className="mt-4 text-center">
              <p className="text-valia-gray-600 text-sm">
                Already have an account?{" "}
                <Link
                  to="/signin"
                  className="text-valia-accent hover:text-valia-accent-light font-medium transition-colors duration-200"
                >
                  Sign In
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;
